import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import { COMPANY_NAME, PHONE, SITE_DOMAIN, SITE_NAME, SITE_URL } from '../constants/site';

const UPDATED = 'May 25, 2026';

export default function ServiceFeesPage() {
  return (
    <PolicyLayout title="Service Fees" lastUpdated={UPDATED}>
      <PolicySection title="1. Overview">
        <p>
          This Service Fees Policy explains how {SITE_NAME} ({SITE_URL}, &ldquo;{SITE_DOMAIN}&rdquo;), operated by{' '}
          {COMPANY_NAME}, may charge fees when you search, book, or receive assistance for travel services. Fees are
          disclosed before you complete a transaction whenever applicable.
        </p>
        <p>
          Travel suppliers (airlines, hotels, car rental companies, cruise lines, and package providers) set their own
          base prices, taxes, and carrier-imposed charges. {SITE_NAME} may add a separate service or agency fee for
          facilitating your booking and providing customer support.
        </p>
      </PolicySection>

      <PolicySection title="2. Types of service fees">
        <PolicySubsection title="2.1 Booking service fees">
          <p>
            A per-passenger or per-reservation booking fee may apply when you complete a purchase through{' '}
            {SITE_DOMAIN}. This fee compensates {COMPANY_NAME} for search technology, payment processing coordination,
            confirmation delivery, and 24/7 support access.
          </p>
        </PolicySubsection>
        <PolicySubsection title="2.2 Processing fees">
          <p>
            Payment processing fees may be included in the total displayed at checkout or shown as a separate line item,
            depending on the payment method, card type, and supplier settlement requirements.
          </p>
        </PolicySubsection>
        <PolicySubsection title="2.3 Assisted-booking fees">
          <p>
            If you book by phone with a live agent, additional assisted-booking fees may apply. These are quoted before
            your card is charged.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="3. When fees are disclosed">
        <PolicyList
          items={[
            'Search results show indicative totals; final fees appear on the review and payment screens before you submit.',
            'Your confirmation email and itinerary receipt list itemized charges where provided by the supplier and our platform.',
            'Promotional or advertised fares may exclude certain fees until the fare rules and checkout summary are displayed.',
          ]}
        />
      </PolicySection>

      <PolicySection title="4. Fees by product type">
        <PolicySubsection title="4.1 Flights">
          <p>
            Flight bookings may include agency service fees in addition to airline base fare, government taxes, and
            carrier-imposed surcharges (fuel, baggage, seat selection, etc.). Fee amounts vary by route, cabin, fare type,
            and whether the ticket is refundable or restricted.
          </p>
        </PolicySubsection>
        <PolicySubsection title="4.2 Hotels, cars, packages, and cruises">
          <p>
            Non-air products may include a service fee or commission embedded in the displayed rate. Resort fees, local
            taxes, deposits, and mandatory charges set by the property or supplier are separate and are your
            responsibility at check-in or pickup.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="5. Non-refundable service fees">
        <p>
          Unless required by law or stated otherwise at checkout, service fees charged by {COMPANY_NAME} are generally
          non-refundable even when the underlying travel component is canceled or refunded by the supplier. Supplier
          refunds follow the fare rules shown at booking; see our{' '}
          <Link to="/flight/cancellation-refund">Cancellation &amp; Refund Policy</Link> for details.
        </p>
      </PolicySection>

      <PolicySection title="6. Waivers and adjustments">
        <p>
          We may waive or adjust fees in cases of verified technical errors, duplicate charges, or goodwill resolutions at
          our discretion. Fee disputes must be reported within thirty (30) days of the transaction date.
        </p>
      </PolicySection>

      <PolicySection title="7. Related policies">
        <p>
          See also: <Link to="/flight/post-ticketing-fees">Post Ticketing Fees</Link>,{' '}
          <Link to="/flight/fare-disclosure">Fare Disclosure Policy</Link>, and{' '}
          <Link to="/flight/terms">Terms &amp; Conditions</Link>.
        </p>
      </PolicySection>

      <PolicySection title="8. Contact us">
        <p>
          Questions about fees on a specific booking? Call <a href={`tel:${PHONE.replace(/[^\d+]/g, '')}`}>{PHONE}</a>{' '}
          or use the contact details below.
        </p>
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
